/**
 * Salinan bahasa Indonesia untuk lapisan "near me" — 9 layanan utama.
 *
 * Ditulis manual (tidak memakai AI credit saat build maupun runtime). Fakta
 * selaras dengan ./registry.ts dan ./en-copy.ts:
 *  - Thailand BUKAN anggota Konvensi Apostille Den Haag, sehingga dokumen Thailand
 *    untuk dipakai di luar negeri dilegalisasi di Departemen Konsuler, Kementerian
 *    Luar Negeri Thailand, lalu bila diminta dilegalisasi lagi di kedutaan negara
 *    tujuan yang ada di Thailand.
 *  - Tidak ada harga yang dipublikasikan (penawaran per kasus), tidak ada janji
 *    waktu selesai, dan tidak ada jaminan diterima — keputusan ada pada instansi
 *    penerima.
 */

import type { NearServiceKey } from "./registry";

export type NearIdCopy = {
  /** Nama layanan singkat untuk H1/title. */
  label: string;
  /** Definisi 1-2 kalimat untuk blok jawaban langsung. */
  definition: string;
  /** Urutan pengerjaan. */
  chain: string[];
  /** Dokumen yang perlu disiapkan. */
  checklist: string[];
  /** Kesalahan yang sering membuat berkas ditolak. */
  pitfalls: string[];
  /** Cakupan pengerjaan jarak jauh. */
  remote: string;
};

export const NEAR_ID_KEYS = [
  "notary",
  "naati",
  "sworn",
  "pcc",
  "translation",
  "moj",
  "visa",
  "lgz",
  "apostille",
] as const satisfies readonly NearServiceKey[];

export type NearIdKey = (typeof NEAR_ID_KEYS)[number];

const MFA =
  "Bila instansi penerima memintanya, berkas dilanjutkan ke Departemen Konsuler (Kementerian Luar Negeri Thailand), lalu ke kedutaan negara tujuan di Thailand.";

export const NEAR_ID_COPY: Record<NearIdKey, NearIdCopy> = {
  notary: {
    label: "Notarial Services Attorney Thailand (pengesahan dokumen)",
    definition:
      "Thailand tidak memiliki kantor notaris terpisah. Pengesahan tanda tangan, salinan, affidavit, dan surat kuasa perusahaan dilakukan oleh advokat berlisensi Notarial Services Attorney dari Lawyers Council of Thailand, umumnya untuk dokumen yang akan dipakai di luar negeri.",
    chain: [
      "Kirim scan warna seluruh dokumen dan sebutkan instansi penerima atau negara tujuan.",
      "Kami menentukan halaman mana yang benar-benar perlu disahkan dan dalam urutan apa.",
      "Penanda tangan menandatangani di hadapan notarial attorney (hadir langsung atau format lain yang diterima instansi).",
      "Pencatatan klausul pengesahan, pembubuhan cap, dan pendaftaran di buku register.",
      MFA,
      "Dokumen asli yang sudah disahkan dikirim atau diambil di kantor.",
    ],
    checklist: [
      "Paspor atau KTP Thailand tiap penanda tangan (masih berlaku, ejaan nama sama dengan dokumen).",
      "Dokumen dalam versi final dan belum ditandatangani.",
      "Akta/company affidavit DBD dan daftar direktur berwenang bila menandatangani atas nama perusahaan.",
      "Surat kuasa bila menandatangani untuk orang lain.",
      "Permintaan tertulis dari instansi penerima, jika Anda sudah memilikinya.",
    ],
    pitfalls: [
      "Menandatangani sebelum jadwal pengesahan, sehingga dokumen asli harus dibuat ulang.",
      "Ejaan nama berbeda dari paspor — penyebab penolakan paling umum di luar negeri.",
      "Menganggap dokumen Thailand bisa diberi Apostille; kenyataannya melewati legalisasi Kemlu dan kedutaan.",
      "Mengesahkan fotokopi padahal instansi meminta dokumen asli bersertifikat.",
      "Company affidavit terlalu lama, sedangkan banyak instansi meminta terbitan baru.",
    ],
    remote:
      "Peninjauan berkas, pemeriksaan klausul, dan tahap legalisasi lanjutan bisa dikerjakan jarak jauh; hanya proses tanda tangan yang perlu kehadiran.",
  },
  naati: {
    label: "Terjemahan bersertifikat NAATI",
    definition:
      "Terjemahan NAATI dikerjakan penerjemah bersertifikat NAATI (badan akreditasi penerjemah Australia) dengan klausul dan cap penerjemah — biasanya syarat wajib instansi pemerintah Australia, universitas, dan pusat pengurusan visa.",
    chain: [
      "Kirim scan warna; kami periksa ketajaman, cap, dan jumlah halaman.",
      "Konfirmasi instansi penerima (Home Affairs, universitas, badan lisensi) dan ejaan nama sesuai paspor.",
      "Penerjemah NAATI mengerjakan terjemahan lengkap dengan klausul dan cap.",
      "Pemeriksaan mutu terhadap dokumen sumber: nama, tanggal, angka, tulisan pada cap.",
      "Penyerahan berkas digital bersertifikat dan, bila perlu, salinan cetak via pos.",
    ],
    checklist: [
      "Halaman paspor untuk mengambil ejaan nama Latin yang benar.",
      "Scan warna lengkap tiap dokumen, termasuk halaman belakang dan cap.",
      "Terjemahan lama (bila ada) agar istilah tetap konsisten.",
      "Ketentuan terjemahan dari instansi penerima, jika diberikan kepada Anda.",
    ],
    pitfalls: [
      "Scan terpotong sehingga cap tidak terlihat dan set terjemahan tidak sah.",
      "Nama yang sama ditulis berbeda antar dokumen.",
      "Menerjemahkan dokumen padahal instansi butuh dokumen asli yang disahkan lebih dulu.",
      "Mengirim scan hitam-putih sehingga warna cap tidak bisa diverifikasi.",
    ],
    remote: "Seluruh proses daring — tidak perlu datang.",
  },
  sworn: {
    label: "Penerjemah tersumpah / terdaftar di pengadilan",
    definition:
      "Banyak negara hanya menerima terjemahan dari penerjemah tersumpah atau terdaftar resmi. Di Thailand pekerjaan ini dilakukan penerjemah yang tercatat pada daftar instansi atau pengadilan terkait, dengan halaman klausul yang menyebut identitas penerjemah dan dokumen sumber.",
    chain: [
      "Pastikan daftar mana yang diakui negara tujuan (daftar pengadilan, kedutaan, atau kementerian).",
      "Tinjau scan dari sisi ketajaman dan kelengkapan.",
      "Penerjemah terdaftar mengerjakan terjemahan dan halaman klausul.",
      "Opsional: legalisasi di Kementerian Luar Negeri dan pengesahan di kedutaan negara tujuan.",
      "Pengiriman set cetak yang sudah dicap.",
    ],
    checklist: [
      "Paspor atau KTP untuk transliterasi nama yang tepat.",
      "Scan lengkap termasuk lampiran dan seluruh cap.",
      "Nama instansi penerima dan pasangan bahasa yang dibutuhkan.",
      "Tenggat penyerahan yang ditetapkan instansi penerima.",
    ],
    pitfalls: [
      "Memakai penerjemah biasa padahal negara tujuan hanya menerima penerjemah terdaftar.",
      "Halaman klausul hilang — justru bagian pertama yang diperiksa instansi.",
      "Mencampur beberapa gaya transliterasi antar dokumen.",
      "Lampiran yang dirujuk dokumen sumber tidak disertakan.",
    ],
    remote:
      "Hampir semua tahap kami kerjakan untuk Anda; hanya tahap tanda tangan langsung yang butuh kehadiran.",
  },
  pcc: {
    label: "SKCK Thailand (Police Clearance Certificate)",
    definition:
      "Police Clearance Certificate Thailand diterbitkan Royal Thai Police dan mencatat ada atau tidak adanya catatan pidana dalam basis data Thailand. Instansi visa, izin tinggal permanen, lisensi profesi, dan perekrutan di luar negeri sering memintanya beserta terjemahan bersertifikat.",
    chain: [
      "Pastikan instansi yang meminta dan tujuan penggunaan yang akan dicantumkan di formulir.",
      "Siapkan berkas: identitas, bukti tujuan penggunaan, dan foto.",
      "Pengambilan sidik jari — harus hadir langsung atau memakai format luar negeri yang diterima.",
      "Pengajuan ke Royal Thai Police dan pemantauan status.",
      "Terjemahan bersertifikat, lalu legalisasi Kemlu dan kedutaan bila diperlukan.",
    ],
    checklist: [
      "Paspor (serta KTP Thailand atau izin kerja bila ada).",
      "Bukti tujuan — surat visa, permintaan pemberi kerja, atau formulir instansi.",
      "Foto terbaru sesuai spesifikasi yang diminta.",
      "Dokumen tempat tinggal atau riwayat keluar-masuk bila pemohon warga asing.",
      "Surat kuasa bila pengajuan diwakilkan.",
    ],
    pitfalls: [
      "Tujuan penggunaan tidak cocok dengan instansi peminta sehingga sertifikat tidak terpakai.",
      "Lembar sidik jari dibuat dalam format yang tidak diterima.",
      "Sertifikat terbit terlalu lama sebelum diajukan, padahal instansi punya batas masa berlaku.",
      "Lupa bahwa tahap terjemahan dan legalisasi membutuhkan waktu tersendiri.",
    ],
    remote:
      "Sidik jari harus dilakukan langsung; pengajuan, pemantauan, terjemahan, dan legalisasi kami kerjakan untuk Anda.",
  },
  translation: {
    label: "Terjemahan bersertifikat (semua bahasa)",
    definition:
      "Terjemahan bersertifikat adalah terjemahan utuh yang dilengkapi klausul bertanda tangan bahwa isinya sesuai dokumen sumber. Siapa yang berhak menyertifikasi — perusahaan penerjemahan, penerjemah terdaftar, atau penerjemah dalam daftar kedutaan — ditentukan instansi penerima.",
    chain: [
      "Peninjauan scan dan penawaran per kasus.",
      "Penetapan istilah dan transliterasi nama sesuai paspor.",
      "Penerjemahan, lalu penyuntingan independen dibandingkan dokumen sumber.",
      "Penerbitan dan pembubuhan cap pada halaman klausul.",
      "Opsional: legalisasi Kemlu dan pengesahan kedutaan negara tujuan.",
    ],
    checklist: [
      "Scan warna semua halaman, sisi depan dan belakang.",
      "Halaman paspor sebagai acuan ejaan nama.",
      "Bahasa target dan instansi penerima.",
      "Terjemahan sebelumnya agar rumusan tetap konsisten.",
    ],
    pitfalls: [
      "Ada halaman dokumen berhalaman banyak yang tidak diterjemahkan.",
      "Menerjemahkan sebelum cap resmi dibubuhkan sehingga cap tidak muncul di terjemahan.",
      "Nama pada sertifikat, paspor, dan formulir tidak sama.",
      "Menganggap satu jenis sertifikasi berlaku di semua negara — syaratnya berbeda-beda.",
    ],
    remote: "Sepenuhnya daring; salinan cetak dikirim pos bila dokumen asli diperlukan.",
  },
  moj: {
    label: "Dokumen Kementerian Kehakiman dan instansi pemerintah",
    definition:
      "Urusan di Kementerian Kehakiman dan instansi terkait meliputi salinan catatan, permohonan, dan sertifikat yang diproses instansi Thailand. Tiap prosedur punya formulir, daftar bukti, dan antrean sendiri, jadi urutan yang benar lebih penting daripada kecepatan.",
    chain: [
      "Menentukan instansi, divisi, dan formulir yang tepat.",
      "Menghimpun daftar bukti dan memeriksa dokumen identitas.",
      "Mengajukan berkas dan mencatat nomor referensi.",
      "Memantau proses dan menanggapi permintaan tambahan dari instansi.",
      "Terjemahan bersertifikat dan legalisasi bila dokumen dibawa ke luar negeri.",
    ],
    checklist: [
      "Paspor atau KTP Thailand pemohon.",
      "Kartu keluarga (tabien baan) atau dokumen perusahaan, tergantung jenis prosedur.",
      "Bukti pendukung yang disebut dalam formulir instansi.",
      "Surat kuasa bila pengajuan diwakilkan.",
    ],
    pitfalls: [
      "Mengajukan ke divisi yang salah sehingga harus mengantre dari awal.",
      "Bukti pendukung kurang — penyebab keterlambatan paling sering.",
      "Mengabaikan masa berlaku dokumen pendukung.",
      "Menerjemahkan sebelum dokumen asli berbahasa Thai difinalkan.",
    ],
    remote:
      "Pengajuan dan pemantauan kami kerjakan untuk Anda pada prosedur yang mengizinkan perwakilan.",
  },
  visa: {
    label: "Penyiapan dokumen visa dan keimigrasian",
    definition:
      "Pekerjaan dokumen visa adalah penyiapan dan konsultasi berkas: menghimpun, menerjemahkan, dan mengesahkan set dokumen yang diminta konsulat atau kantor imigrasi. Keputusan akhir selalu milik instansi yang menilai.",
    chain: [
      "Konfirmasi jenis visa dan daftar dokumen yang berlaku saat ini.",
      "Membandingkan dengan dokumen yang sudah Anda miliki untuk menemukan kekurangan.",
      "Terjemahan bersertifikat dan pengesahan untuk dokumen yang memerlukannya.",
      "Menyusun berkas sesuai urutan daftar instansi, dilengkapi lembar cek saat pengajuan.",
      "Pendampingan saat pengajuan dan penanganan permintaan dokumen tambahan.",
    ],
    checklist: [
      "Paspor dengan masa berlaku cukup dan halaman visa sebelumnya.",
      "Dokumen catatan sipil — pernikahan, kelahiran, perceraian — bila jenis visa memintanya.",
      "Bukti keuangan dalam format yang ditetapkan instansi.",
      "Surat kerja, surat studi, atau surat sponsor.",
      "SKCK dan surat keterangan medis bila diperlukan.",
    ],
    pitfalls: [
      "Memakai daftar dokumen lama dari blog, bukan daftar resmi terbaru instansi.",
      "Bukti keuangan salah format atau salah periode.",
      "Urutan pengesahan salah sehingga klausul tidak mencakup versi final.",
      "Mengharap jaminan lolos — tidak ada konsultan yang bisa menjaminnya.",
    ],
    remote:
      "Penyiapan dan peninjauan dikerjakan jarak jauh; wawancara dan biometrik perlu kehadiran.",
  },
  lgz: {
    label: "Legalisasi Kementerian Luar Negeri Thailand (Departemen Konsuler)",
    definition:
      "Legalisasi di Departemen Konsuler, Kementerian Luar Negeri Thailand adalah pengesahan tanda tangan dan cap pada dokumen resmi Thailand serta terjemahannya. Karena Thailand bukan anggota Apostille, tahap ini menjadi langkah standar sebelum kedutaan negara tujuan mengesahkan berkas.",
    chain: [
      "Pastikan negara tujuan meminta legalisasi dokumen asli Thai, terjemahannya, atau keduanya.",
      "Ambil dokumen asli baru bila instansi meminta terbitan terkini.",
      "Siapkan terjemahan bersertifikat, termasuk isi tulisan pada cap.",
      "Pengajuan di Departemen Konsuler dan pengambilan hasil.",
      "Pengesahan di kedutaan negara tujuan bila instansi penerima memintanya.",
    ],
    checklist: [
      "Dokumen resmi asli Thailand bertanda tangan dan bercap.",
      "Paspor atau KTP pemilik nama pada dokumen.",
      "Terjemahan bersertifikat bila negara tujuan butuh set terjemahan.",
      "Surat kuasa untuk pengajuan yang diwakilkan.",
    ],
    pitfalls: [
      "Menunggu Apostille untuk dokumen Thailand — Thailand tidak ikut konvensi tersebut.",
      "Terjemahan melewatkan tulisan pada cap.",
      "Menyerahkan fotokopi padahal dibutuhkan dokumen asli dari instansi penerbit.",
      "Melegalisasi sebelum negara tujuan memastikan set mana yang diminta, sehingga harus diulang.",
    ],
    remote: "Pengajuan dan pengambilan kami tangani; Anda cukup mengirim dokumen asli.",
  },
  apostille: {
    label: "Apostille dan legalisasi dokumen Thailand",
    definition:
      "Apostille adalah satu sertifikat tunggal yang berlaku antar negara anggota Konvensi Den Haag. Thailand bukan anggota, sehingga dokumen Thailand harus dilegalisasi di Departemen Konsuler dan, bila perlu, disahkan di kedutaan negara tujuan di Thailand. Dokumen asing yang sudah ber-Apostille tetap bisa memerlukan terjemahan dan pengesahan untuk dipakai di Thailand.",
    chain: [
      "Tentukan arah penggunaan: dokumen Thailand ke luar negeri, atau dokumen asing masuk ke Thailand.",
      "Berkas keluar: terjemahan bersertifikat, legalisasi Kemlu, lalu pengesahan kedutaan bila perlu.",
      "Berkas masuk: periksa Apostille asing, lalu terjemahan bahasa Thai bersertifikat dan pengesahan sisi Thailand bila perlu.",
      "Konfirmasi rumusan syarat terbaru instansi penerima sebelum pengajuan.",
      "Penyerahan set berkas lengkap dengan lembar cek untuk pengajuan.",
    ],
    checklist: [
      "Dokumen dalam status sertifikasi terkini, termasuk Apostille asing bila ada.",
      "Paspor pemilik nama untuk pencocokan ejaan.",
      "Permintaan tertulis instansi penerima, bila tersedia.",
      "Informasi negara tujuan dan tujuan penggunaan.",
    ],
    pitfalls: [
      "Membayar untuk \"Apostille\" pada dokumen Thailand — sertifikat jenis itu tidak bisa diterbitkan.",
      "Menganggap Apostille asing menggantikan terjemahan bahasa Thai bersertifikat.",
      "Melewatkan pengesahan kedutaan padahal instansi penerima masih memintanya.",
      "Berpegang pada panduan lama sementara ketentuan sudah berubah.",
    ],
    remote:
      "Penilaian dan seluruh rantai pengesahan dikerjakan jarak jauh; Anda hanya perlu mengirimkan dokumen asli kepada kami.",
  },
};

/** Nama provinsi/kawasan Thailand dalam ejaan bahasa Indonesia. */
export const NEAR_ID_AREA: Record<string, string> = {
  nonthaburi: "Nonthaburi",
  pathumthani: "Pathum Thani",
  samutprakan: "Samut Prakan",
  chonburi: "Chonburi",
  pattaya: "Pattaya",
  rayong: "Rayong",
  chiangmai: "Chiang Mai",
  chiangrai: "Chiang Rai",
  khonkaen: "Khon Kaen",
  udonthani: "Udon Thani",
  nakhonratchasima: "Nakhon Ratchasima (Korat)",
  phuket: "Phuket",
  suratthani: "Surat Thani",
  songkhla: "Songkhla",
  ubonratchathani: "Ubon Ratchathani",
};
